import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import {
  MemoryDTO,
  StepCheckOperatorType,
  CheckOperator,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { SubCondition, HubConditionOperator } from 'dorian-shared/types/branch/HubOption';
import { HubValueSelect } from './HubValueSelect';
import './SubConditionRow.scss';

/** Operators available for hub state conditions */
export const HUB_CONDITION_OPERATORS = [
  StepCheckOperatorType.Equal,
  StepCheckOperatorType.NotEqual,
  StepCheckOperatorType.Greater,
  StepCheckOperatorType.AtLeast,
  StepCheckOperatorType.Less,
  StepCheckOperatorType.AtMost,
] as const;

interface SubConditionRowProps {
  subCondition: SubCondition;
  memoryVariables: MemoryDTO[];
  isDisabled: boolean;
  onChange: (updated: SubCondition) => void;
  onRemove: () => void;
  canRemove: boolean;
}

/**
 * Generic presentational component for one sub-condition row:
 * [variable ▾] [operator ▾] [value ▾] ×
 *
 * No State-specific logic. Reusable by V1.1 condition sections.
 */
export function SubConditionRow({
  subCondition,
  memoryVariables,
  isDisabled,
  onChange,
  onRemove,
  canRemove,
}: SubConditionRowProps) {
  const [varSearchFilter, setVarSearchFilter] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleDropdownToggle = useCallback((isOpen: boolean) => {
    if (isOpen) {
      // Focus search input after dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setVarSearchFilter('');
    }
  }, []);

  const selectedVariable = useMemo(
    () => memoryVariables.find((v) => v.id === subCondition.variableId),
    [memoryVariables, subCondition.variableId],
  );

  const filteredVariables = useMemo(() => {
    const withoutRightHand = subCondition.varValue !== undefined
      ? memoryVariables.filter((v) => v.id !== subCondition.varValue)
      : memoryVariables;
    if (!varSearchFilter) return withoutRightHand;
    const lower = varSearchFilter.toLowerCase();
    return withoutRightHand.filter(
      (v) =>
        v.name.toLowerCase().includes(lower) ||
        v.displayName.toLowerCase().includes(lower),
    );
  }, [memoryVariables, varSearchFilter, subCondition.varValue]);

  const handleVariableSelect = useCallback(
    (variableId: number) => {
      onChange({ ...subCondition, variableId });
      setVarSearchFilter('');
    },
    [subCondition, onChange],
  );

  const handleOperatorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...subCondition, operator: e.target.value as HubConditionOperator });
    },
    [subCondition, onChange],
  );

  const handleValueChange = useCallback(
    (data: { intValue?: string; varValue?: number }) => {
      // Enforce mutual exclusion: when switching to variable mode, clear intValue; when switching to literal mode, clear varValue
      const updated = { ...subCondition, ...data };
      if (data.varValue !== undefined) {
        // varValue 0 = "not set" per spec — treat as clearing the variable selection
        updated.varValue = data.varValue === 0 ? undefined : data.varValue;
        updated.intValue = undefined;
      } else if (data.intValue !== undefined) {
        updated.varValue = undefined;
      }
      onChange(updated);
    },
    [subCondition, onChange],
  );

  const hasNoVariables = memoryVariables.length === 0;

  return (
    <div className="sub-condition-row" data-testid="sub-condition-row">
      {/* Memory variable selector */}
      <Dropdown className="sub-condition-row__variable" onToggle={handleDropdownToggle}>
        <Dropdown.Toggle
          variant="outline-primary"
          size="sm"
          disabled={isDisabled || hasNoVariables}
          className="sub-condition-row__variable-toggle"
          data-testid="variable-select"
        >
          {hasNoVariables
            ? 'No variables'
            : selectedVariable?.displayName || selectedVariable?.name || 'Select variable'}
        </Dropdown.Toggle>
        <Dropdown.Menu className="sub-condition-row__variable-menu">
          <div className="px-2 pb-1">
            <Form.Control
              ref={searchInputRef}
              size="sm"
              placeholder="Search..."
              value={varSearchFilter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVarSearchFilter(e.target.value)}
            />
          </div>
          {filteredVariables.length === 0 && (
            <Dropdown.Item disabled>No variables found</Dropdown.Item>
          )}
          {filteredVariables.map((v) => (
            <Dropdown.Item
              key={v.id}
              active={v.id === subCondition.variableId}
              onClick={() => handleVariableSelect(v.id)}
            >
              {v.displayName || v.name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {/* Operator selector */}
      <Form.Control
        as="select"
        size="sm"
        className="sub-condition-row__operator"
        value={subCondition.operator}
        onChange={handleOperatorChange}
        disabled={isDisabled}
        data-testid="operator-select"
      >
        {HUB_CONDITION_OPERATORS.map((op) => (
          <option key={op} value={op}>
            {CheckOperator[op].title}
          </option>
        ))}
      </Form.Control>

      {/* Value input */}
      <div className="sub-condition-row__value">
        <HubValueSelect
          subConditionUuid={subCondition.uuid}
          intValue={subCondition.intValue}
          varValue={subCondition.varValue}
          variables={memoryVariables}
          excludeVariableId={subCondition.variableId ?? undefined}
          onChange={handleValueChange}
          disabled={isDisabled}
          placeholder="Value or variable"
        />
      </div>

      {/* Remove button */}
      <button
        type="button"
        className="btn btn-sm btn-link sub-condition-row__remove"
        onClick={onRemove}
        disabled={isDisabled || !canRemove}
        title="Remove sub-condition"
        data-testid="remove-sub-condition"
      >
        ×
      </button>
    </div>
  );
}
